import * as React from 'react';
import { ClickAwayListener, ListItemIcon, Menu, MenuItem } from '@mui/material';
import cn from 'classnames';

import { Icons } from '../icons';
import { Tooltip } from '../tooltip';
import type { IPopoverProps } from './popover.component';

import './popoverMenu.css';

/**
 * Represents an item in the menu.
 */
export interface IMenuItem {
  /**
   * The callback function invoked when the menu item is clicked.
   */
  onClick?: () => void;
  /**
   * The href link, when this is present the linkComponent passed to the parent will be used
   */
  href?: string;
  /**
   * The content of the menu item. It can be a string or a React node.
   */
  content: string | React.ReactNode;
  /**
   * The icon displayed with the menu item.
   */
  icon?: React.ReactNode;
  /**
   * A boolean representing whether a line separator is rendered above the item.
   */
  separator?: boolean;
  /**
   * Whether the menu item is disabled
   */
  disabled?: boolean;
  /**
   *  Whether the menu item is hidden
   */
  hidden?: boolean;

  /**
   * When provided, wraps the content with an info icon that shows this text on hover — commonly
   * used to explain why a `disabled` item is disabled.
   */
  tooltip?: string;

  /**
   * An optional nested menu. When present, clicking the item drills into a sub-menu (with a title
   * and a back button) instead of invoking `onClick`. A chevron affordance is shown on the item.
   */
  subMenu?: {
    /** Title displayed at the top of the sub-menu, next to the back button. */
    title: string;
    /** The items shown within the sub-menu. */
    items: IMenuItem[];
  };

  unmaskForAnalytics?: boolean;
}

/**
 * Props for the PopoverMenu component.
 */
export interface IPopoverMenuProps extends IPopoverProps {
  /**
   * An array of menu items to be displayed in the popover menu.
   */
  menuItems: IMenuItem[];
  /**
   * The link component to render when a href is passed to a menu item
   */
  linkComponent?: React.ElementType;
  /**
   * If true, the popover menu will close when the mouse leaves the menu area. Defaults to true.
   */
  closeOnMouseLeave?: boolean;
}

/**
 * A React component that renders a menu inside a popover.
 *
 * @param ref - A ref to the underlying HTMLDivElement.
 * @param {IPopoverMenuProps} props - Other props passed to the component.
 * @returns The rendered PopoverMenu component.
 */
const PopoverMenuComponent: React.ForwardRefRenderFunction<HTMLDivElement, IPopoverMenuProps> = (
  { menuItems, linkComponent, onClickAway, closeOnMouseLeave = true, ...rest },
  ref
) => {
  const timeoutId = React.useRef<NodeJS.Timeout>(undefined);
  const [activeSubMenu, setActiveSubMenu] = React.useState<IMenuItem['subMenu']>(undefined);
  const [direction, setDirection] = React.useState<'forward' | 'back'>('forward');
  const getLinkProps = (href?: string) =>
    href && linkComponent
      ? {
          href,
          component: linkComponent,
        }
      : {};

  const openSubMenu = (subMenu: NonNullable<IMenuItem['subMenu']>) => {
    setDirection('forward');
    setActiveSubMenu(subMenu);
  };

  const closeSubMenu = () => {
    setDirection('back');
    setActiveSubMenu(undefined);
  };

  React.useEffect(() => {
    return () => clearTimeout(timeoutId.current);
  }, []);

  // Reset to the root menu whenever the popover opens so it always starts at the top level. Doing
  // this on open (rather than on close) avoids a visible transition while the menu is closing.
  React.useEffect(() => {
    if (rest.open) {
      setDirection('forward');
      setActiveSubMenu(undefined);
    }
  }, [rest.open]);

  const itemsToRender = activeSubMenu?.items ?? menuItems;

  return (
    <ClickAwayListener
      onClickAway={() => {
        onClickAway?.();
        rest.onClose();
      }}
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
    >
      <Menu
        ref={ref}
        {...rest}
        classes={{
          root: 'seccl-popover-menu',
          list: 'seccl-popover-menu-list',
        }}
        // if `closeOnMouseLeave` is true, close the menu after 500ms when the mouse leaves the menu area
        {...(closeOnMouseLeave
          ? {
              onMouseLeave: () => {
                timeoutId.current = setTimeout(() => {
                  rest.onClose();
                }, 500);
              },
              // if the mouse re-enters the menu area, cancel the close timeout
              onMouseOver: () => {
                clearTimeout(timeoutId.current);
              },
            }
          : {})}
        aria-modal
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
          ...(rest.anchorOrigin ?? {}),
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
          ...(rest.transformOrigin ?? {}),
        }}
      >
        <div className={cn('seccl-popover-menu-panel', `seccl-popover-menu-panel-${direction}`)}>
          {activeSubMenu && (
            <MenuItem
              className="seccl-menu-item seccl-popover-submenu-title"
              disableTouchRipple
              aria-label={`Back from ${activeSubMenu.title}`}
              onClick={event => {
                event.stopPropagation();
                closeSubMenu();
              }}
            >
              <span className="seccl-menu-item-chevron">
                <Icons.ChevronLeft />
              </span>
              <span className="seccl-menu-item-content">{activeSubMenu.title}</span>
            </MenuItem>
          )}
          {itemsToRender.map((item, i) => {
            if (item.hidden) return null;
            return (
              <MenuItem
                {...(item.unmaskForAnalytics ? { 'data-clarity-unmask': true } : {})}
                data-separator={item.separator}
                className="seccl-menu-item"
                disableTouchRipple
                key={i}
                disabled={item.disabled}
                onClick={event => {
                  event.stopPropagation();
                  if (item.subMenu) {
                    openSubMenu(item.subMenu);
                    return;
                  }
                  item.onClick?.();
                  rest.onClose();
                }}
                {...getLinkProps(item.href)}
              >
                {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                <span className="seccl-menu-item-content">
                  {item.tooltip ? (
                    // MUI disables pointer-events on the whole item when `disabled`, which would also
                    // block hover on the tooltip's info icon — re-enable it just for this content.
                    <span style={{ pointerEvents: 'auto' }}>
                      <Tooltip title={item.tooltip} infoIcon>
                        <span>{item.content}</span>
                      </Tooltip>
                    </span>
                  ) : (
                    item.content
                  )}
                </span>
                {item.subMenu && (
                  <span className="seccl-menu-item-chevron">
                    <Icons.ChevronRight />
                  </span>
                )}
              </MenuItem>
            );
          })}
        </div>
      </Menu>
    </ClickAwayListener>
  );
};

/**
 * A forwardRef wrapper for the PopoverMenuComponent.
 */
export const PopoverMenu = React.forwardRef(PopoverMenuComponent);
